<template>
  <div class="container-fluid px-4 mt-5">
    <!-- Fixed Position Alerts -->
    <!-- Success Message -->
    <transition name="fade">
      <div v-if="showSuccessMessage" class="alert alert-success alert-dismissible fade show" role="alert" style="position: fixed; top: 20px; right: 20px; z-index: 1050; max-width: 400px; min-width: 300px;">
        <div class="d-flex align-items-start">
          <i class="fas fa-check-circle me-2 mt-1"></i>
          <div class="flex-grow-1">
            {{ successMessage }}
          </div>
          <button type="button" @click="showSuccessMessage = false" aria-label="Close" style="background: none; border: 1px solid #c3e6cb; border-radius: 50%; width: 20px; height: 20px; display: flex; align-items: center; justify-content: center; margin-left: 8px; cursor: pointer;">
            <span style="color: #155724; font-size: 14px; font-weight: bold;">&times;</span>
          </button>
        </div>
      </div>
    </transition>

    <!-- Error Message -->
    <transition name="fade">
      <div v-if="showErrorMessage" class="alert alert-danger alert-dismissible fade show" role="alert" style="position: fixed; top: 20px; right: 20px; z-index: 1050; max-width: 500px; min-width: 300px;">
        <div class="d-flex align-items-start">
          <i class="fas fa-exclamation-circle me-2 mt-1"></i>
          <div class="flex-grow-1">
            <pre style="white-space: pre-wrap; margin: 0; font-family: inherit;">{{ errorMessage }}</pre>
          </div>
          <button type="button" @click="showErrorMessage = false" aria-label="Close" style="background: none; border: 1px solid #f5c6cb; border-radius: 50%; width: 20px; height: 20px; display: flex; align-items: center; justify-content: center; margin-left: 8px; cursor: pointer;">
            <span style="color: #721c24; font-size: 14px; font-weight: bold;">&times;</span>
          </button>
        </div>
      </div>
    </transition>

    <div class="card shadow rounded-lg">
      <div class="card-header bg-light d-flex justify-content-between align-items-center border-0 py-3">
        <h4 class="mb-0 fw-normal text-muted">
          <i :class="assessmentIcon" class="me-2"></i>
          Edit {{ assessment.assessment_type }}: {{ assessment.name }}
        </h4>
        <button
          type="button"
          class="btn btn-sm btn-outline-secondary"
          @click="$router.push(`/courses/${courseId}/subjects/${subjectId}/edit`)"
        >
          <i class="fas fa-arrow-left me-1"></i> Back to Edit Subject
        </button>
      </div>
      <div class="card-body">

        <div v-if="loading" class="text-center py-4">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <p class="mt-2">Loading assessment data...</p>
        </div>

        <div v-else class="row">
          <!-- Left Side - Assessment Details -->
          <div class="col-lg-4 pe-lg-4 border-lg-end mb-4 mb-lg-0">
            <form @submit.prevent="submitEdit">
              <div class="row mb-3">
                <label class="col-md-4 col-form-label">Course Name</label>
                <div class="col-md-8">
                  <input
                    type="text"
                    :value="courseName"
                    class="form-control bg-light"
                    disabled
                  />
                </div>
              </div>

              <div class="row mb-3">
                <label class="col-md-4 col-form-label">Subject Name</label>
                <div class="col-md-8">
                  <input
                    type="text"
                    :value="subjectName"
                    class="form-control bg-light"
                    disabled
                  />
                </div>
              </div>

              <div class="row mb-3">
                <label class="col-md-4 col-form-label">{{ assessment.assessment_type }} Name</label>
                <div class="col-md-8">
                  <input
                    v-model.trim="assessment.name"
                    type="text"
                    class="form-control"
                    :class="{ 'is-invalid': errors.name }"
                    required
                  />
                  <div v-if="errors.name" class="invalid-feedback">
                    {{ errors.name }}
                  </div>
                </div>
              </div>

              <div class="row mb-3">
                <label class="col-md-4 col-form-label">Description</label>
                <div class="col-md-8">
                  <textarea
                    v-model.trim="assessment.description"
                    class="form-control"
                    :class="{ 'is-invalid': errors.description }"
                    rows="3"
                    required
                  ></textarea>
                  <div v-if="errors.description" class="invalid-feedback">
                    {{ errors.description }}
                  </div>
                </div>
              </div>

              <div class="row mb-3">
                <label class="col-md-4 col-form-label">Grade Percentage</label>
                <div class="col-md-8">
                  <input
                    v-model.number="assessment.grade_percentage"
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    class="form-control"
                    :class="{ 'is-invalid': errors.grade_percentage }"
                    required
                    @input="updateTotalGradeLeft"
                  />
                  <div v-if="errors.grade_percentage" class="invalid-feedback">
                    {{ errors.grade_percentage }}
                  </div>
                  <small class="text-muted">Total grade left: {{ totalGradeLeft }}%</small>
                </div>
              </div>

              <div class="row mb-3">
                <div class="col-md-8 offset-md-4">
                  <button
                    type="submit"
                    class="btn px-4"
                    :disabled="submitting"
                    style="background-color: #00004d; color: white;"
                  >
                    <span v-if="submitting" class="spinner-border spinner-border-sm me-1"></span>
                    <i v-else class="fas fa-save me-1"></i>
                    {{ submitting ? 'Saving...' : 'Save Changes' }}
                  </button>
                </div>
              </div>
            </form>
          </div>

          <!-- Right Side - Grades Management -->
          <div class="col-lg-8 ps-lg-4">
            <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
             <h5 class="mb-0">
               <i class="fas fa-graduation-cap me-2"></i>
               Student Grades ({{ filteredGrades.length }} students)
             </h5>
                  <div class="d-flex gap-2 flex-wrap">
           <div class="input-group" style="max-width: 250px; height: 10px;">
            <span class="input-group-text"><i class="fas fa-search"></i></span>
              <input 
                type="text" 
                class="form-control" 
                placeholder="Search students..." 
                v-model="searchQuery"
               @input="currentPage = 1"
            >
          </div>
              <button
                v-if="grades.length > 0 && assessment.assessment_type !== 'Attendance'"
                type="button"
                class="btn btn-sm btn-primary"
                @click="saveGrades"
                :disabled="savingGrades || !gradesModified"
              >
                <span v-if="savingGrades" class="spinner-border spinner-border-sm me-1"></span>
                <i v-else class="fas fa-save me-1"></i>
                {{ savingGrades ? 'Saving...' : 'Save Grades' }}
              </button>
             <div class="d-flex align-items-center mb-3 flex-wrap gap-2">
             <div class="btn-group btn-group-sm" role="group">
              <button 
               class="btn btn-outline-secondary" 
               @click="downloadTemplate"
               :disabled="downloadingTemplate"
              >
               <i v-if="!downloadingTemplate" class="fas fa-file-excel me-1"></i>
               <span v-else class="spinner-border spinner-border-sm me-1"></span>
               {{ downloadingTemplate ? 'Preparing...' : 'Download Template' }}
              </button>
              <button 
               class="btn btn-outline-secondary" 
               @click="importGrades"
               :disabled="importingGrades"
              >
               <i v-if="!importingGrades" class="fas fa-file-import me-1"></i>
               <span v-else class="spinner-border spinner-border-sm me-1"></span>
               {{ importingGrades ? 'Importing...' : 'Import Grades' }}
              </button>
              <button 
               class="btn btn-outline-secondary" 
               @click="exportGrades"
              :disabled="exportingGrades"
              >
             <i v-if="!exportingGrades" class="fas fa-file-export me-1"></i>
               <span v-else class="spinner-border spinner-border-sm me-1"></span>
               {{ exportingGrades ? 'Exporting...' : 'Export Grades' }}
            </button>
              </div>
              </div>
              <div v-if="assessment.assessment_type === 'Attendance'">
              <div v-if="erpConnectionFailed" class="alert alert-warning">
                  <i class="fas fa-exclamation-triangle me-2"></i>
                  Could not load attendance data automatically. Please enter attendance grades manually.
              </div>
  
             <div v-if="assessment.assessment_type === 'Attendance'">
                <div class="alert alert-info mb-3">
                   <i class="fas fa-info-circle me-2"></i>
                     Attendance grades are automatically calculated and cannot be manually edited.
                     The value shown represents attendance percentage.
                </div>
            </div>
             </div>
            </div>

            <div v-if="gradesLoading" class="text-center py-4">
              <div class="spinner-border spinner-border-sm text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
              <p class="mt-2">Loading student grades...</p>
            </div>

            <div v-else-if="grades.length === 0" class="alert alert-info">
              <i class="fas fa-info-circle me-2"></i>
              No students enrolled in this course yet
            </div>

            <div v-else>
              <div class="table-responsive">
                <table class="table table-hover w-100">
                  <thead>
                    <tr>
                      <th width="20%">ID</th>
                      <th width="40%">Student Name</th>
                      <th width="30%">Grade</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="grade in paginatedGrades" :key="grade.id">
                     <td @click="searchByErpId(grade.student_erp_id)" 
                        style="cursor: pointer;"
                       :title="grade.student_erp_id === 'Not Available' ? 'Student ID not available' : ''"
                       :class="{ 'text-muted': grade.student_erp_id === 'Not Available' }">
                        {{ grade.student_erp_id }}
                     </td>
                      <td>{{ grade.full_name }}</td>
                      <td>
                        <input
                          v-model.number="grade.value"
                          type="number"
                          min="0"
                          :max="maxGradeValue"
                          class="form-control form-control-sm"
                          :class="{ 'is-invalid': gradeErrors[grade.id] }"
                          @input="gradesModified = true"
                          :disabled="assessment.assessment_type === 'Attendance'"

                        />
                        <div v-if="gradeErrors[grade.id]" class="invalid-feedback d-block">
                          {{ gradeErrors[grade.id] }}
                        </div>
                      </td>
                      
                    </tr>
                  </tbody>
                </table>
              </div>
              <div class="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
              <div class="text-muted">
                Showing {{ (currentPage - 1) * pageSize + 1 }} to {{ Math.min(currentPage * pageSize, filteredGrades.length) }} of {{ filteredGrades.length }} entries
              </div>
              <nav aria-label="Page navigation" v-if="totalPages > 1">
                <ul class="pagination mb-0">
                  <li class="page-item" :class="{ disabled: currentPage === 1 }">
                    <button class="page-link" @click="prevPage">
                      <i class="fas fa-chevron-left"></i>
                    </button>
                  </li>
                  <li 
                    class="page-item" 
                    v-for="page in visiblePages" 
                    :key="page"
                    :class="{ active: currentPage === page }"
                  >
                    <button class="page-link" @click="goToPage(page)">{{ page }}</button>
                  </li>
                  <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                    <button class="page-link" @click="nextPage">
                      <i class="fas fa-chevron-right"></i>
                    </button>
                  </li>
              
                </ul>
              </nav>
            </div>
            </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <input 
    type="file" 
    ref="fileInput" 
    style="display: none;" 
    accept=".xlsx,.xls"
    @change="handleFileUpload"
  >
</template>

<script>
import axios from "axios";

const API_BASE_URL = process.env.VUE_APP_API_URL || 'http://localhost:3000';

export default {
  name: 'EditAssessment',
  data() {
    return {
      assessment: {
        _id: "",
        name: "",
        description: "",
        grade_percentage: 0,
        assessment_type: "",
        assessment_type_id: "",
        subject_id: "",
        course_id: ""
      },
      searchQuery: '',
      subjectName: "",
      courseName: "",
      courseId: "",
      subjectId: "",
      grades: [],
      downloadingTemplate: false,
      importingGrades: false,
      exportingGrades: false,
      loading: true,
      gradesLoading: false,
      submitting: false,
      savingGrades: false,
      showSuccessMessage: false,
      showErrorMessage: false,
      successMessage: "",
      errorMessage: "",
      errors: {
        name: "",
        description: "",
        grade_percentage: "",
        general: ""
      },
      gradeErrors: {},
      maxGradeValue: 100,
      currentPage: 1,
      pageSize: 10,
      totalGrades: 0,
      totalAssignedGrade: 0, // Sum of all OTHER assessments' percentages
      gradesModified: false,
      originalGrades: []
    };
  },
  computed: {
    assessmentIcon() {
      return this.assessment.assessment_type === 'Exam' ? 'fas fa-file-alt' : 'fas fa-tasks';
    },
     filteredGrades() {
      if (!this.searchQuery) return this.grades;
      const query = this.searchQuery.toLowerCase();
      return this.grades.filter(grade => 
        grade.full_name.toLowerCase().includes(query) || 
        (grade.student_erp_id && grade.student_erp_id.toString().toLowerCase().includes(query))
      );
    },
     paginatedGrades() {
    const start = (this.currentPage - 1) * this.pageSize;
    const end = start + this.pageSize;
    return this.filteredGrades.slice(start, end);
  },
    totalPages() {
      return Math.ceil(this.grades.length / this.pageSize);
    },
    totalGradeLeft() {
      // Calculate dynamically: 100% - (sum of all OTHER assessments + current assessment's new value)
      const currentGrade = parseFloat(this.assessment.grade_percentage) || 0;
      return (100 - (this.totalAssignedGrade + currentGrade)).toFixed(2);
    },
     visiblePages() {
      const range = 1;
      const start = Math.max(1, this.currentPage - range);
      const end = Math.min(this.totalPages, this.currentPage + range);
      
      let pages = [];
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      if (start > 1) {
        pages.unshift(1);
        if (start > 2) pages.splice(1, 0, '...');
      }
      if (end < this.totalPages) {
        if (end < this.totalPages - 1) pages.push('...');
        pages.push(this.totalPages);
      }
      
      return pages;
    },
  },
  methods: {
     async downloadTemplate() {
      try {
        this.downloadingTemplate = true;
        const authToken = localStorage.getItem("auth_token");
        if (!authToken) {
          this.handleUnauthorized();
          return;
        }

        const response = await axios.get(
          `${API_BASE_URL}/api/assessments/${this.assessment._id}/template`,
          {
            headers: { Authorization: `Bearer ${authToken}` },
            responseType: 'blob'
          }
        );

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${this.assessment.name}_Grades_Template.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();

        this.showSuccess("Template downloaded successfully!");
      } catch (err) {
        console.error("Failed to download template:", err);
        this.handleDownloadError(err, "download template");
      } finally {
        this.downloadingTemplate = false;
      }
    },

    importGrades() {
      this.$refs.fileInput.click();
    },

    async handleFileUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      try {
        event.target.value = ''; // Reset file input
        this.importingGrades = true;
        const authToken = localStorage.getItem("auth_token");
        if (!authToken) {
          this.handleUnauthorized();
          return;
        }

        const formData = new FormData();
        formData.append('file', file);

        await axios.post(
          `${API_BASE_URL}/api/assessments/${this.assessment._id}/import`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );

        this.showSuccess("Grades imported successfully!");
        await this.fetchGrades();
        this.gradesModified = false;
      } catch (err) {
        console.error("Failed to import grades:", err);
        this.handleImportError(err);
      } finally {
        this.importingGrades = false;
      }
    },

    async exportGrades() {
      try {
        this.exportingGrades = true;
        const authToken = localStorage.getItem("auth_token");
        if (!authToken) {
          this.handleUnauthorized();
          return;
        }

        const response = await axios.get(
          `${API_BASE_URL}/api/assessments/${this.assessment._id}/export`,
          {
            headers: { Authorization: `Bearer ${authToken}` },
            responseType: 'blob'
          }
        );

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `${this.assessment.name}_Grades_Export_${new Date().toISOString().split('T')[0]}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.remove();

        this.showSuccess("Grades exported successfully!");
      } catch (err) {
        console.error("Failed to export grades:", err);
        this.handleDownloadError(err, "export grades");
      } finally {
        this.exportingGrades = false;
      }
    },

    async fetchAssessment() {
      this.loading = true;
      const assessmentId = this.$route.params.assessmentId;
      this.subjectId = this.$route.params.subjectId;
      this.courseId = this.$route.params.courseId;
      
      try {
        const authToken = localStorage.getItem("auth_token");
        if (!authToken) {
          this.redirectToLogin();
          return;
        }

        const headers = {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        };

        // Fetch assessment, subject, and course in parallel
        const [assessmentResponse, subjectResponse, courseResponse, assessmentsResponse] = await Promise.all([
          axios.get(`${API_BASE_URL}/api/assessments/${assessmentId}`, { headers }),
          axios.get(`${API_BASE_URL}/api/subjects/${this.subjectId}`, { headers }),
          axios.get(`${API_BASE_URL}/api/courses/${this.courseId}`, { headers }),
          axios.get(`${API_BASE_URL}/api/subjects/${this.subjectId}/assessments`, { headers })
        ]);

        // Handle nested response structures
        this.assessment = assessmentResponse.data.assessment || assessmentResponse.data;
        this.subjectName = subjectResponse.data.subject?.name || subjectResponse.data.name || "Unknown Subject";
        this.courseName = courseResponse.data.courses?.[0]?.name || 
                         courseResponse.data.course?.name || 
                         courseResponse.data.name || 
                         "Unknown Course";

        // Calculate total assigned grade EXCLUDING current assessment
        const allAssessments = assessmentsResponse.data.assessments || assessmentsResponse.data || [];
        this.totalAssignedGrade = allAssessments.reduce((sum, a) => {
            const percentage = parseFloat(a.grade_percentage?.$numberDecimal || a.grade_percentage);
            if (a._id !== this.assessment._id) {
            return sum + (isNaN(percentage) ? 0 : percentage);
          }
          return sum;
        }, 0); 
              
          this.totalGradeLeft = (100 - this.totalAssignedGrade).toFixed(2);

        // Set max grade value based on assessment type
        this.maxGradeValue = this.assessment.assessment_type === 'Exam' ? 100 : this.assessment.grade_percentage || 100;

        // Fetch grades separately
        await this.fetchGrades();

      } catch (error) {
        this.handleAssessmentError(error);
      } finally {
        this.loading = false;
      }
    },

// Removed duplicate fetchGrades method
    updateTotalGradeLeft() {
      this.assessment.grade_percentage = parseFloat(this.assessment.grade_percentage) || 0;
    },

    async submitEdit() {
      if (!this.validateForm()) return;

      this.submitting = true;

      try {
        const authToken = localStorage.getItem("auth_token");
        const headers = {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        };

        const payload = {
          name: this.assessment.name,
          description: this.assessment.description,
          grade_percentage: parseFloat(this.assessment.grade_percentage)
        };

        await axios.put(
          `${API_BASE_URL}/api/assessments/${this.assessment._id}`,
          payload,
          { headers }
        );

        this.showSuccess("Assessment updated successfully!");
        // Update max grade value if it's an assignment
        if (this.assessment.assessment_type !== 'Exam') {
          this.maxGradeValue = this.assessment.grade_percentage;
        }
      } catch (error) {
        console.error("Error updating assessment:", error);
        if (error.response) {
          if (error.response.status === 404) {
            this.showError("Assessment not found. It may have been deleted.");
            setTimeout(() => {
              this.$router.push(`/courses/${this.courseId}/subjects/${this.subjectId}/edit`);
            }, 2000);
          } else {
            this.showError(error.response.data.error || "Failed to update assessment");
          }
        } else {
          this.showError("Network error. Please check your connection.");
        }
      } finally {
        this.submitting = false;
      }
    },

    async fetchGrades() {
  this.gradesLoading = true;
  try {
    const authToken = localStorage.getItem("auth_token");
    const headers = {
      Authorization: `Bearer ${authToken}`,
      'Content-Type': 'application/json'
    };

    const response = await axios.get(
      `${API_BASE_URL}/api/assessments/${this.assessment._id}/grades`,
      { headers }
    );

    console.log("Grades API response:", response.data);

    this.grades = (response.data || []).map(grade => {
      // Try to find the ID in different possible fields
      const erpId = grade.student_erp_id || 
                    grade.student_erp_id || 
                    grade.erp_id || 
                    grade.user_id || 
                    grade.id;
      
      return {
        ...grade,
        student_erp_id: erpId || 'Not Available',
        full_name: grade.full_name || grade.name || 'Unknown Student'
      };
    });

  } catch (error) {
    console.error("Error fetching grades:", error);
    this.showError("Failed to load student grades. Please try again.");
    this.grades = [];
    this.totalGrades = 0;
  } finally {
    this.gradesLoading = false;
  }
},
 async saveGrades() {
      if (this.assessment.assessment_type === 'Attendance') {
        this.showInfo("Attendance grades are automatically calculated and cannot be manually edited");
        return;
      }
      if (!this.validateGrades()) return;

      this.savingGrades = true;
      
      try {
        const authToken = localStorage.getItem("auth_token");
        const headers = {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        };

        const gradeUpdates = this.grades
          .filter(grade => {
            const original = this.originalGrades.find(g => g.id === grade.id);
            return !original || original.value !== grade.value;
          })
          .map(grade => ({
            id: grade.id,
            value: grade.value === null || grade.value === undefined ? 0 : grade.value
          }));

        if (gradeUpdates.length === 0) {
          this.showInfo("No grade changes to save.");
          return;
        }

        await axios.post(
          `${API_BASE_URL}/api/assessments/${this.assessment._id}/grades`,
          gradeUpdates,
          { headers }
        );

        this.showSuccess("Grades saved successfully!");
        this.gradesModified = false;
        // Update original grades
        this.originalGrades = JSON.parse(JSON.stringify(this.grades));
      } catch (error) {
        console.error("Error saving grades:", error);
        if (error.response) {
          if (error.response.status === 400) {
            this.showError("Invalid grade values. Please check your inputs.");
          } else if (error.response.status === 404) {
            this.showError("Assessment not found. It may have been deleted.");
          } else {
            this.showError(error.response.data.error || "Failed to save grades");
          }
        } else {
          this.showError("Network error. Please check your connection.");
        }
      } finally {
        this.savingGrades = false;
      }
    },
    validateForm() {
      let isValid = true;
      this.errors = { name: "", description: "", grade_percentage: "", general: "" };

      if (!this.assessment.name.trim()) {
        this.errors.name = `${this.assessment.assessment_type} name is required`;
        isValid = false;
      }

      if (!this.assessment.description.trim()) {
        this.errors.description = "Description is required";
        isValid = false;
      }

      const gradePercentage = parseFloat(this.assessment.grade_percentage);
      if (isNaN(gradePercentage)) {
        this.errors.grade_percentage = "Grade percentage must be a number";
        isValid = false;
      } else if (gradePercentage < 0 || gradePercentage > 100) {
        this.errors.grade_percentage = "Grade percentage must be between 0 and 100";
        isValid = false;
      } else if (gradePercentage > (100 - this.totalAssignedGrade)) {
        this.errors.grade_percentage = `Grade percentage cannot exceed ${(100 - this.totalAssignedGrade).toFixed(2)}% (remaining available)`;
        isValid = false;
      }

      return isValid;
    },

    validateGrades() {
      let isValid = true;
      this.gradeErrors = {};

      this.grades.forEach(grade => {
        const value = parseFloat(grade.value);
        if (grade.value !== null && grade.value !== undefined) {
          if (isNaN(value) || value < 0 || value > this.maxGradeValue) {
            this.gradeErrors[grade.id] = `Grade must be between 0 and ${this.maxGradeValue}`;
            isValid = false;
          }
          if (this.assessment.assessment_type === 'Attendance') {
        if (value > 100) {
          this.gradeErrors[grade.id] = "Attendance percentage cannot exceed 100%";
          isValid = false;
        }
        }
        }
      });

      return isValid;
    },

    handleAssessmentError(error) {
      console.error("Assessment error:", error);
      if (error.response?.status === 404) {
        this.showError(`${this.assessment.assessment_type} not found - it may have been deleted`);
        setTimeout(() => {
          this.$router.push(`/courses/${this.courseId}/subjects/${this.subjectId}/edit`);
        }, 2000);
      } else {
        this.showError("Failed to load assessment data. Please try again.");
      }
    },

    redirectToLogin() {
      localStorage.removeItem("auth_token");
      this.$router.push("/login");
    },

    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },

    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },

    goToPage(page) {
      if (page !== '...') {
        this.currentPage = page;
      }
    },
     searchByErpId(erpId) {
      this.searchQuery = erpId;
      this.currentPage = 1;
    },
    showSuccess(message) {
      this.successMessage = message;
      this.showSuccessMessage = true;
      setTimeout(() => {
        this.showSuccessMessage = false;
      }, 3000);
    },

    showError(message) {
      this.errorMessage = message;
      this.showErrorMessage = true;
      // Remove auto-dismiss timeout to keep error persistent
      // setTimeout(() => {
      //   this.showErrorMessage = false;
      // }, 3000);
    },

    handleImportError(err) {
      let errorMessage = "Failed to import grades";
      
      if (err.response?.data) {
        const data = err.response.data;
        
        // Handle detailed error response from backend
        if (data.error && (data.errors || data.warnings || data.summary)) {
          errorMessage = data.error;
          
          // Add detailed errors if they exist
          if (data.errors && data.errors.length > 0) {
            errorMessage += "\n\nDetailed Errors:";
            data.errors.forEach((error, index) => {
              errorMessage += `\n${index + 1}. ${error}`;
            });
          }
          
          // Add warnings if they exist
          if (data.warnings && data.warnings.length > 0) {
            errorMessage += "\n\nWarnings:";
            data.warnings.forEach((warning, index) => {
              errorMessage += `\n${index + 1}. ${warning}`;
            });
          }
          
          // Add summary if it exists
          if (data.summary) {
            errorMessage += `\n\nSummary: ${data.summary.totalProcessed || 0} grades processed`;
            if (data.summary.totalAdded) errorMessage += `, ${data.summary.totalAdded} added`;
            if (data.summary.totalUpdated) errorMessage += `, ${data.summary.totalUpdated} updated`;
          }
        } else if (data.error) {
          errorMessage = data.error;
        } else if (data.message) {
          errorMessage = data.message;
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      this.showError(errorMessage);
    },

    handleDownloadError(err, action = "download file") {
      let errorMessage = `Failed to ${action}`;
      
      if (err.response?.data) {
        // Handle blob error responses for download/export
        if (err.response.data instanceof Blob) {
          try {
            const reader = new FileReader();
            reader.onload = () => {
              try {
                const errorData = JSON.parse(reader.result);
                if (errorData.error) {
                  this.showError(errorData.error);
                } else {
                  this.showError(errorMessage);
                }
              } catch (parseError) {
                this.showError(`${errorMessage} (${err.response?.status}: ${err.response?.statusText})`);
              }
            };
            reader.readAsText(err.response.data);
            return;
          } catch (blobError) {
            errorMessage = `${errorMessage} (${err.response?.status}: ${err.response?.statusText})`;
          }
        } else {
          const data = err.response.data;
          if (data.error) {
            errorMessage = data.error;
          } else if (data.message) {
            errorMessage = data.message;
          }
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      this.showError(errorMessage);
    },

    showInfo(message) {
      this.successMessage = message;
      this.showSuccessMessage = true;
      setTimeout(() => {
        this.showSuccessMessage = false;
      }, 2000);
    }
  },
  async mounted() {
    await this.fetchAssessment();
    
    // Check for success messages from previous page
    if (this.$route.query.success) {
      this.showSuccess(this.$route.query.success);
      // Clean the URL
      const cleanQuery = {...this.$route.query};
      delete cleanQuery.success;
      this.$router.replace({query: cleanQuery});
    }
  }
};
</script>

<style scoped>
.text-muted {
  color: #6c757d;
  font-style: italic;
}
td[style*="cursor: pointer"]:hover {
  text-decoration: underline;
  color: #0d6efd;
}
.card-header {
  font-weight: 600;
}

.invalid-feedback {
  display: block;
}

.form-control.is-invalid, .form-select.is-invalid {
  border-color: #dc3545;
}
.btn-outline-secondary {
  border-color: #6c757d;
  color: #6c757d;
}

.btn-outline-secondary:hover {
  background-color: #6c757d;
  color: white;
}

.btn-group .btn {
  white-space: nowrap;
}

@media (max-width: 992px) {
  .btn-group {
    width: 100%;
    margin-bottom: 10px;
  }
  
  .btn-group .btn {
    flex: 1;
    text-align: center;
  }
  
  .input-group {
    width: 100%;
    max-width: 100% !important;
  }

  .form-control {
    padding: 0.75rem;
  }
  
  .btn {
    padding: 0.5rem 1rem;
    font-size: 1rem;
  }
  @media (max-width: 768px) {
  .btn-group .btn {
    padding-left: 0.5rem;
    padding-right: 0.5rem;
    font-size: 0.85rem;
  }
}
  
  .table input[type="number"] {
    width: 80px !important;
    padding: 0.5rem;
  }
}

.alert {
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 2000;
  max-width: 350px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  animation: slideIn 0.3s ease-out forwards;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}

.border-end {
  border-right: 1px solid #dee2e6 !important;
}

.table th, .table td {
  vertical-align: middle;
}

.page-item.active .page-link {
  background-color: #00004d;
  border-color: #00004d;
}

.page-link {
  color: #00004d;
}

.text-muted {
  font-size: 0.8rem;
}

.table-responsive {
  width: 100%;
  overflow-x: auto;
}

.table {
  width: 100%;
  min-width: 600px;
}

.badge {
  font-size: 0.8rem;
  padding: 0.35em 0.65em;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.btn-primary {
  background: linear-gradient(90deg, orange, red);
  border-color: #ff6600;
  border-color: #00004d;
}

.btn-primary:hover {
  background-color: #000033;
  border-color: #000033;
}

</style>
